import { provide } from "@lit/context";
import { ExportFormat } from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { enabledFeatures, type EnabledFeatures, loadEnabledFeatures } from "../../../contexts/features-context";
import { shared } from "../../../styles";

@customElement("ptc-exports-headers")
export class ExportsHeaders extends LitElement {
    @provide({ context: enabledFeatures })
    @state()
    private _features: EnabledFeatures = loadEnabledFeatures();

    @property({ type: String })
    page: ExportFormat;

    static styles = [
        shared,
        css`
            .text-embedded-icon {
                transform: translateY(2px);
            }

            .text-embedded-icon > ptc-ui-icon {
                transform: translateY(2px);
                line-height: 1em;
                height: 1em;
            }
        `,
    ];

    render() {
        switch (this.page) {
            case ExportFormat.PayrollReportDetailed:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohnbericht (Detailliert)</h2>
                        <div class="subtle margined">
                            Dieser Bericht kommt in PDF-Form und enthält <strong>Arbeitszeiten</strong>,
                            <strong>Abwesenheiten</strong>, <strong>Zuschläge</strong>,
                            <strong>Vertragsänderungen</strong> und alle anderen lohn-relevanten Informationen innerhalb
                            des gewählten Zeitraums inklusive einer <strong>Auswertung</strong>.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollReportOverview:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohnbericht (Übersicht)</h2>
                        <div class="subtle margined">
                            Dieser Bericht kommt in PDF-Form und enthält <strong>Arbeitszeiten</strong>,
                            <strong>Abwesenheiten</strong>, <strong>Zuschläge</strong>,
                            <strong>Vertragsänderungen</strong> und alle anderen lohn-relevanten Informationen innerhalb
                            des gewählten Zeitraums inklusive einer <strong>Auswertung</strong>.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollDatevLG:
                return html`
                    <div class="text-centering">
                        <h2>
                            <ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohndatenexport Datev Lohn &
                            Gehalt
                        </h2>
                        <div class="subtle margined">
                            Eine Textdatei, die über die sogannte
                            <strong>ASCII-Schnittstelle</strong> von
                            <a
                                href="https://www.datev.de/web/de/datev-shop/personalwirtschaft/lohn-und-gehalt-comfort/"
                                target="_blank"
                            >
                                Datev Lohn & Gehalt
                            </a>
                            eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollDatevLodas:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohndatenexport Datev Lodas</h2>
                        <div class="subtle margined">
                            Eine Textdatei, die über die sogannte <strong>ASCII-Schnittstelle</strong> von
                            <a
                                href="https://www.datev.de/web/de/datev-shop/personalwirtschaft/lodas-comfort/"
                                target="_blank"
                            >
                                Datev Lodas
                            </a>
                            eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollAgenda:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohndatenexport Agenda</h2>
                        <div class="subtle margined">
                            Eine Textdatei, die über die <strong>ASCII-Schnittstelle</strong> von
                            <a href="https://www.agenda-software.de/unternehmen/lohn-software.php" target="_blank">
                                Agenda
                            </a>
                            eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollAgendaEmployeeCoreData:
                return html`
                    <div class="text-centering">
                        <h2>
                            <ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Agenda Export
                            Mitarbeiter-Stammdaten
                        </h2>
                        <div class="subtle margined">
                            <div class="subtle margined">
                                Eine Excel-Datei mit den <strong>Stammdaten</strong> der Mitarbeiter, die über die
                                <strong>Import-Maske</strong> von
                                <a href="https://agenda-kunden.de/hilfe/1608.php" target="_blank"> Agenda </a>
                                eingelesen werden kann.
                            </div>
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollAgendaCalendar:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Agenda Export Kalenderdaten</h2>
                        <div class="subtle margined">
                            <div class="subtle margined">
                                Eine Excel-Datei mit tagesgenauen <strong>Urlaubs- und Kranktagen</strong>, die über die
                                <strong>Import-Maske</strong> von
                                <a href="https://agenda-kunden.de/hilfe/1778.php" target="_blank"> Agenda </a>
                                eingelesen werden kann. Es werden nur <strong>genehmigte Abwesenheiten</strong> des
                                gewählten Monats exportiert. "Frei" und "Guttag" werden nicht exportiert.
                            </div>
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollLexware:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohndatenexport Lexware</h2>
                        <div class="subtle margined">
                            Eine Textdatei, die über die sogannte <strong>ASCII-Schnittstelle</strong> von
                            <a href="https://shop.lexware.de/lohn" target="_blank">Lexware Lohn und Gehalt</a>
                            eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollAddison:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohndatenexport Addison</h2>
                        <div class="subtle margined">
                            Eine Textdatei, die über die sogannte <strong>ASCII-Schnittstelle</strong> von
                            <a
                                href="https://www.wolterskluwer.com/de-de/solutions/addison-komplettloesung-mittelstand/addison-lohn-und-gehaltsabrechnung-sme"
                                target="_blank"
                            >
                                Addison
                            </a>
                            eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollLohnAG:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohndatenexport LohnAG</h2>
                        <div class="subtle margined">
                            Eine Textdatei, die über die sogannte <strong>ASCII-Schnittstelle</strong> von
                            <a href="https://www.lohn-ag.de/" target="_blank"> LohnAG </a>
                            eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollSage:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohndatenexport Sage</h2>
                        <div class="subtle margined">
                            Eine Textdatei, die über die sogannte <strong>ASCII-Schnittstelle</strong> von
                            <a href="https://www.sage.de/" target="_blank"> Sage </a>
                            eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollBMDWages:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> Lohndatenexport BMD</h2>
                        <div class="subtle margined">
                            Eine CSV-Datei mit Lohndaten, die über die <strong>Schnittstelle</strong> von
                            <a
                                href="https://www.bmd.at/Portaldata/1/Resources/help/27.25/OES/Documents/1165317561018012300.html#Textmarke_4"
                                target="_blank"
                            >
                                BMD
                            </a>
                            eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.PayrollBMDAbsences:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.labour-cost"></ptc-ui-icon> BMD Nichtleistungszeiten</h2>
                        <div class="subtle margined">
                            Eine CSV-Datei mit angefallenen Nichtleistungszeiten, die über die
                            <strong>Schnittstelle</strong> von
                            <a
                                href="hhttps://www.bmd.at/Portaldata/1/Resources/help/27.01/OES/Documents/1173103919002700700.html"
                                target="_blank"
                            >
                                BMD
                            </a>
                            eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.TimeSheet:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.time-history"></ptc-ui-icon> Arbeitszeit-Nachweis</h2>
                        <div class="subtle margined">
                            Eine Auflistung aller <strong>Arbeitszeiten</strong> und <strong>Fehltage</strong> zur
                            Aushändigung an und Unterschrift durch den Mitarbeiter.
                        </div>
                    </div>
                `;
            case ExportFormat.CashbookDatev:
                return html`
                    <div class="text-centering">
                        <h2>
                            <ptc-ui-icon name="icon.object.cashbook"></ptc-ui-icon> Kassenbuch-Export DATEV
                            Buchungsstapel
                        </h2>
                        <div class="subtle margined">
                            Exportiert alle <strong>Kassenbucheinträge</strong> für den gewählten Standort und Zeitraum
                            im <strong>DATEV Buchungstapel</strong> Format, welches direkt nach DATEV importiert werden
                            kann.
                        </div>
                    </div>
                `;
            case ExportFormat.CashbookDatevOnline:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.cashbook"></ptc-ui-icon> Kassenbuch-Export DATEV Online</h2>
                        <div class="subtle margined">
                            Exportiert alle <strong>Kassenbucheinträge</strong> für den gewählten Standort und Zeitraum
                            in einem Format, welches direkt nach <strong>DATEV Online</strong> importiert werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.CashbookLexware:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.cashbook"></ptc-ui-icon> Kassenbuch-Export Lexware</h2>
                        <div class="subtle margined">
                            Exportiert alle <strong>Kassenbucheinträge</strong> für den gewählten Standort und Zeitraum
                            im ein Format, welches direkt in das Buchhaltungs Lexware eingelesen werden kann.
                        </div>
                    </div>
                `;
            case ExportFormat.CashbookGeneric:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.cashbook"></ptc-ui-icon> Kassenbuch-Export Generisch</h2>
                        <div class="subtle margined">
                            Exportiert alle <strong>Kassenbucheinträge</strong> für den gewählten Standort und Zeitraum
                            als generisches CSV-Format.
                        </div>
                    </div>
                `;
            case ExportFormat.EmployeeDataPDF:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.master-data"></ptc-ui-icon> Mitarbeiter Datenblatt</h2>
                        <div class="subtle margined">
                            Die Stammdaten Ihrer Mitarbeiter auf einer übersichtlichen PDF-Seite.
                        </div>
                    </div>
                `;
            case ExportFormat.EmployeeDataCSV:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.master-data"></ptc-ui-icon> Mitarbeiter Stammdaten</h2>
                        <div class="subtle margined">
                            Die Stammdaten Ihrer Mitarbeiter als tabellarischer CSV-Export.
                        </div>
                    </div>
                `;
            case ExportFormat.BalancesTimeExcel:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.time-balance"></ptc-ui-icon> Arbeitszeitkonto</h2>
                        <div class="subtle margined">
                            Dieser Export enthält eine tabellarische Darstellung der
                            <strong>Arbeitszeitkonten</strong> für den gewählten Zeitraum im Microsoft Excel Format.
                            Über die Option <strong>Auflösung</strong> können Sie außerdem bestimmen, ob die Konten im
                            Wochen-, Monats- oder Jahresrhythmus saldiert werden sollen.
                        </div>
                    </div>
                `;
            case ExportFormat.BalancesVacationExcel:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.time-entry.vacation"></ptc-ui-icon> Urlaubskonto</h2>
                        <div class="subtle margined">
                            Dieser Export enthält eine tabellarische Darstellung der
                            <strong>Urlaubskonten</strong> für den gewählten Zeitraum im Microsoft Excel Format. Über
                            die Option <strong>Auflösung</strong> können Sie außerdem bestimmen, ob die Konten im
                            Wochen-, Monats- oder Jahresrhythmus saldiert werden sollen.
                        </div>
                    </div>
                `;
            case ExportFormat.BalancesBonusesExcel:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.object.bonus"></ptc-ui-icon> Zuschlagskonto</h2>
                        <div class="subtle margined">
                            Dieser Export enthält eine tabellarische Darstellung der
                            <strong>Zuschlagskonten</strong> für den gewählten Zeitraum im Microsoft Excel Format. Über
                            die Option <strong>Auflösung</strong> können Sie außerdem bestimmen, ob die Konten im
                            Wochen-, Monats- oder Jahresrhythmus saldiert werden sollen.
                        </div>
                    </div>
                `;
            case ExportFormat.RosterExcel:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.feature.roster"></ptc-ui-icon> Dienstplan Excel</h2>
                        <div class="subtle margined">
                            Dieser Export enthält eine Darstellung des Dienstplans für den gewählten Zeitraum im
                            Microsoft Excel Format.
                        </div>
                    </div>
                `;
            case ExportFormat.OvertimeExcel:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.feature.roster"></ptc-ui-icon> Mehr- und Überstunden Excel</h2>
                        <div class="subtle margined">
                            Dieser Export enthält eine Auflistung der angefallenen Mehr- und Überstunden für den
                            gewählten Zeitraum im Microsoft Excel Format.
                        </div>
                    </div>
                `;
            case ExportFormat.AverageWorkTimeExcel:
                return html`
                    <div class="text-centering">
                        <h2>
                            <ptc-ui-icon name="icon.feature.roster"></ptc-ui-icon> Durchschnittliche Arbeitszeit Excel
                        </h2>
                        <div class="subtle margined">
                            Dieser Export enthält eine Auflistung der durchschnittlichen Arbeitszeit pro Interval im
                            gewählten Zeitraum im Microsoft Excel Format.
                        </div>
                    </div>
                `;
            case ExportFormat.EmployeeTableExcel:
                return html`
                    <div class="text-centering">
                        <h2><ptc-ui-icon name="icon.feature.roster"></ptc-ui-icon> Individuelle Excel-Tabelle</h2>
                        <div class="subtle margined">
                            Dieser Export bietet die Möglichkeit, Mitarbeiterdaten im Microsoft Excel-Format zu
                            exportieren. Nutzen Sie <strong>Filter</strong> und die
                            <span class="slim transparent semibold text-embedded-icon">
                                <ptc-ui-icon name="icon.toggle.select-all" class="large"></ptc-ui-icon>
                            </span>
                            <strong> Spaltenauswahl</strong>, um die tabellarische Darstellung nach Ihren Bedürfnissen
                            anzupassen.
                        </div>
                    </div>
                `;
        }
    }
}
